import { HttpParameterCodec, HttpParams } from '@angular/common/http';
import { UntypedFormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { OrderDetail, OrderStatus } from '../services/data-order.service';
import { OrderStatusEnum } from './status-options.helper';
import { OrderData } from 'app/views/orders/orders-table/orders-table.component';
import moment, { Moment } from 'moment';

export function getIndexBy(array: Array<{}>, { name, value }): number {
  for (let i = 0; i < array.length; i++) {
    if (array[i][name] === value) {
      return i;
    }
  }
  return -1;
}

function currentYPosition() {
  if (!window) {
    return;
  }
  // Firefox, Chrome, Opera, Safari
  if (window.pageYOffset) return window.pageYOffset;
  // Internet Explorer 6 - standards mode
  if (document.documentElement && document.documentElement.scrollTop)
    return document.documentElement.scrollTop;
  // Internet Explorer 6, 7 and 8
  if (document.body.scrollTop) return document.body.scrollTop;
  return 0;
}

function elmYPosition(elm) {
  var y = elm.offsetTop;
  var node = elm;
  while (node.offsetParent && node.offsetParent !== document.body) {
    node = node.offsetParent;
    y += node.offsetTop;
  }
  return y;
}

export function scrollTo(selector) {
  var elm = document.querySelector(selector);
  if (!selector || !elm) {
    return;
  }
  var startY = currentYPosition();
  var stopY = elmYPosition(elm);
  var distance = stopY > startY ? stopY - startY : startY - stopY;
  if (distance < 100) {
    window.scrollTo(0, stopY);
    return;
  }
  var speed = Math.round(distance / 50);
  if (speed >= 20) speed = 20;
  var step = Math.round(distance / 25);
  var leapY = stopY > startY ? startY + step : startY - step;
  var timer = 0;
  if (stopY > startY) {
    for (var i = startY; i < stopY; i += step) {
      setTimeout(
        (function (leapY) {
          return () => {
            window.scrollTo(0, leapY);
          };
        })(leapY),
        timer * speed
      );
      leapY += step;
      if (leapY > stopY) leapY = stopY;
      timer++;
    }
    return;
  }
  for (let i = startY; i > stopY; i -= step) {
    setTimeout(
      (function (leapY) {
        return () => {
          window.scrollTo(0, leapY);
        };
      })(leapY),
      timer * speed
    );
    leapY -= step;
    if (leapY < stopY) leapY = stopY;
    timer++;
  }
  return false;
}

export const getValueFromKey = <T extends object>(object: T, key: string) => {
  return key.split('.').reduce((obj, str) => {
    return obj?.[str] ?? null;
  }, object);
};

export const removeEmptyProperties = (obj: any): void => {
  Object.keys(obj).forEach(
    key => (obj[key] === '' || obj[key] === undefined || obj[key] === null) && delete obj[key]
  );
};

// Default HttpUrlEncodingCodec leaves "+" raw, which servers parse as space.
// MT-831: searchUser fails to find users with "+" in their email (e.g. jin+111@medmate.com.au).
class FullHttpParameterCodec implements HttpParameterCodec {
  encodeKey(key: string): string { return encodeURIComponent(key); }
  encodeValue(value: string): string { return encodeURIComponent(value); }
  decodeKey(key: string): string { return decodeURIComponent(key); }
  decodeValue(value: string): string { return decodeURIComponent(value); }
}
const FULL_ENCODER = new FullHttpParameterCodec();

export const createHttpParams = (payload: any): HttpParams => {
  let params = new HttpParams({ encoder: FULL_ENCODER });
  for (const key in payload) {
    if (payload.hasOwnProperty(key)) {
      const value = payload[key];
      if (Array.isArray(value)) {
        value.forEach(item => { params = params.append(`${key}[]`, item); });
      } else {
        params = params.append(key, value);
      }
    }
  }
  return params;
};
export interface FormDataPayload {
  [key: string]: string | number | boolean | Blob | File | Array<string | Blob | File> | FormDataPayload;
}

export const toFlag = (input: boolean | null | undefined) => {
  return input ? 1 : 0;
};
export const toBinaryFlag = (input: boolean | null | undefined) => {
  return input ? 1 : 0;
};

export const wait = async (seconds = 0) => {
  await new Promise(resolve => {
    setTimeout(() => {
      resolve(null);
    }, seconds);
  });
};

export const convertPayloadToFormData = (payload: FormDataPayload): FormData => {
  const formData = new FormData();

  const isBlobOrFile = (value: unknown): value is Blob | File => {
    return value instanceof Blob || value instanceof File;
  };

  const appendFormData = (data: FormDataPayload, keyPrefix: string = ''): void => {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const value = data[key];
        const formKey = keyPrefix ? `${keyPrefix}[${key}]` : key;

        if (Array.isArray(value)) {
          value.forEach((element, index) => {
            const arrayKey = `${formKey}[${index}]`;
            if (typeof element === 'string' || isBlobOrFile(element)) {
              formData.append(arrayKey, element);
            } else if (typeof element === 'object' && element !== null) {
              formData.append(arrayKey, JSON.stringify(element));
            }
          });
        } else if (
          typeof value === 'string' ||
          value instanceof Blob ||
          value instanceof File
        ) {
          formData.append(formKey, value);
        } else if (typeof value === 'number') {
          formData.append(formKey, value.toString());
        } else if (typeof value === 'boolean') {
          formData.append(formKey, value ? '1' : '0');
        } else if (typeof value === 'object' && value !== null) {
          formData.append(formKey, JSON.stringify(value));
        }
      }
    }
  };

  appendFormData(payload);
  return formData;
};

export const unescapeNewlineChars = (text: string): string => {
  if (text === null || text === undefined) {
    return '';
  }
  return text.replace(/\\n/g, '\n');
};
export interface ErrorMessage {
  error?: { [key: string]: string[] };
  errors?: { [key: string]: string[] };
  message?: string | { [key: string]: string[] };
  msg?: string;
}

export const getFirstErrorMessage = (errorResponse: ErrorMessage | string = {}): string => {
  if (typeof errorResponse === 'string') {
    return errorResponse;
  }

  // if (errorResponse.msg) {
  //   return errorResponse.msg;
  // }

  // if (errorResponse.error) {
  //   const firstErrorKey = Object.keys(errorResponse.error)[0];
  //   return errorResponse.error[firstErrorKey]?.[0] || 'An unknown error occurred.';
  // }

  // return 'An unknown error occurred.';

  if (errorResponse.msg) {
    return errorResponse.msg;
  }

  if (typeof errorResponse.message === 'string') {
    return errorResponse.message;
  }

  // Collect error messages if there's an error object
  let messages: string[] = [];
  const errorObjects = [errorResponse.error, errorResponse.errors, errorResponse.message]
    .filter(value => typeof value === 'object' && value !== null);

  errorObjects.forEach(errorObject => {
    for (const [key, value] of Object.entries(errorObject)) {
      if (Array.isArray(value)) {
        messages.push(`${key}: ${value.join(', ')}`);
      } else if (typeof value === 'string') {
        messages.push(`${key}: ${value}`);
      }
    }
  });

  // Additional check if `errorResponse` itself contains field-specific errors
  if (typeof errorResponse === 'object' && errorResponse !== null) {
    for (const [key, value] of Object.entries(errorResponse)) {
      if (Array.isArray(value)) {
        messages.push(`${key}: ${value.join(', ')}`);
      } else if (typeof value === 'string' && key !== 'msg') {
        messages.push(`${key}: ${value}`);
      }
    }
  }

  // Join multiple messages or provide a fallback
  return messages.length > 0 ? messages.join(' | ') : 'An unknown error occurred.';
};

export const atLeastOneTagValidator = (): ValidatorFn => {
  return (formGroup: UntypedFormGroup): ValidationErrors | null => {
    const tags = formGroup.get('tags')?.value;
    const customTags = formGroup.get('custom_tags')?.value;

    if (!tags && (!customTags || customTags.length === 0)) {
      return { atLeastOneTagRequired: true };
    }
    return null;
  };
};

export const shouldCreateFollowUpAppointment = (orderStatus: OrderStatus, orderData: OrderDetail['orderData'] | OrderData): boolean => {
  if (orderStatus !== OrderStatusEnum.Completed || orderData.order_class === 'Medmate') {
    return false;
  }

  const sourceKey = orderData.hasOwnProperty('source') ? 'source' : 'Source';
  const source = orderData[sourceKey] || '';

  const excludedSources = ['Follow-Up', 'Care-Call'];
  return !excludedSources.some(excludedSource =>
    source.endsWith(excludedSource) || source === excludedSource
  );
};

export const capitalizeEachWord = (str: string): string => {
  return str
    // Replace underscores and hyphens with spaces
    .replace(/[_-]/g, ' ')
    // Add space before camelCase words
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export const normalizeString = (value: string | undefined | null): string => {
  return value?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
}

export const isOrderTypePickUp = (ordertype: string | undefined | null): boolean => {
  return normalizeString(ordertype) === 'pickup';
}


/**
 * Converts a UTC timestamp string to local time.
 * @param utcTime - The UTC timestamp string (ISO 8601 format recommended).
 * @returns Local time as a Moment object, or `null` if input is invalid.
 */
export const convertToLocalTime = (utcTime: string): Moment | null => {
  if (!utcTime || !moment.utc(utcTime).isValid()) {
    console.warn("Invalid UTC time provided:", utcTime);
    return null;
  }

  return moment.utc(utcTime).local();
};

/**
 * Masks an Australian mobile number (e.g. 0412xx xxx).
 * Supports both 04 and +61 formats.
 *
 * @param mobileNumber - The input number.
 * @returns Masked number or original if invalid.
 */
export const maskMobileNumber = (mobileNumber: string): string => {
  const normalized = mobileNumber.replace(/^\+61/, '0');
  return /^04\d{8}$/.test(normalized)
    ? `${normalized.slice(0, 4)} xx xxx`
    : mobileNumber;
};

/**
 * Converts a UTC timestamp string to a compact user-friendly relative time string (e.g. '2 hrs ago', '3 mins ago', 'in 2 days').
 * @param utcTime - The UTC timestamp string (ISO 8601 format recommended).
 * @returns Compact relative time string, or the original string if invalid.
 */
export const utcToRelativeTime = (utcTime: string): string => {
  const localMoment = convertToLocalTime(utcTime);
  if (!localMoment || !localMoment.isValid()) {
    return utcTime;
  }

  const now = moment();
  const diffSeconds = now.diff(localMoment, 'seconds');

  // If in the future, show "in X" format
  if (diffSeconds < 0) {
    const futureDiffMinutes = localMoment.diff(now, 'minutes');
    const futureDiffHours = localMoment.diff(now, 'hours');
    const futureDiffDays = localMoment.diff(now, 'days');
    const futureDiffMonths = localMoment.diff(now, 'months');
    const futureDiffYears = localMoment.diff(now, 'years');

    if (futureDiffMinutes < 60) return `in ${Math.max(1, futureDiffMinutes)} min${futureDiffMinutes !== 1 ? 's' : ''}`;
    if (futureDiffHours < 24) return `in ${futureDiffHours} hr${futureDiffHours !== 1 ? 's' : ''}`;
    if (futureDiffDays < 30) return `in ${futureDiffDays} day${futureDiffDays !== 1 ? 's' : ''}`;
    if (futureDiffMonths < 12) return `in ${futureDiffMonths} mo${futureDiffMonths !== 1 ? 's' : ''}`;
    return `in ${futureDiffYears} yr${futureDiffYears !== 1 ? 's' : ''}`;
  }

  // Past times
  const diffMinutes = now.diff(localMoment, 'minutes');
  const diffHours = now.diff(localMoment, 'hours');
  const diffDays = now.diff(localMoment, 'days');
  const diffMonths = now.diff(localMoment, 'months');
  const diffYears = now.diff(localMoment, 'years');

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} min${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  if (diffMonths < 12) return `${diffMonths} mo${diffMonths !== 1 ? 's' : ''} ago`;
  return `${diffYears} yr${diffYears !== 1 ? 's' : ''} ago`;
};

/**
 * Converts a UTC timestamp string to exact local date/time (e.g. '21/01/2026 14:35:42').
 * @param utcTime - The UTC timestamp string (ISO 8601 format recommended).
 * @returns Formatted date/time string in DD/MM/YYYY HH:mm:ss format, or the original string if invalid.
 */
export const utcToExactTime = (utcTime: string): string => {
  const localMoment = convertToLocalTime(utcTime);
  return localMoment && localMoment.isValid()
    ? localMoment.format('DD/MM/YYYY HH:mm:ss')
    : utcTime;
};

/**
 * Converts a UTC timestamp string to a compact user-friendly relative time string for future dates (e.g. 'in 2 days', 'in 3 hrs').
 * @param utcTime - The UTC timestamp string (ISO 8601 format recommended).
 * @returns Compact relative time string, or the original string if invalid.
 */
export const utcToFutureRelativeTime = (utcTime: string): string => {
  const localMoment = convertToLocalTime(utcTime);
  if (!localMoment || !localMoment.isValid()) {
    return utcTime;
  }

  const now = moment();
  const diffSeconds = localMoment.diff(now, 'seconds');
  const diffMinutes = localMoment.diff(now, 'minutes');
  const diffHours = localMoment.diff(now, 'hours');
  const diffDays = localMoment.diff(now, 'days');
  const diffMonths = localMoment.diff(now, 'months');
  const diffYears = localMoment.diff(now, 'years');

  // If in the past, use the "ago" helper
  if (diffSeconds < 0) {
    return utcToRelativeTime(utcTime);
  }

  if (diffMinutes < 60) return `in ${diffMinutes} min${diffMinutes !== 1 ? 's' : ''}`;
  if (diffHours < 24) return `in ${diffHours} hr${diffHours !== 1 ? 's' : ''}`;
  if (diffDays < 30) return `in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  if (diffMonths < 12) return `in ${diffMonths} mo${diffMonths !== 1 ? 's' : ''}`;
  return `in ${diffYears} yr${diffYears !== 1 ? 's' : ''}`;
};
